import { useState, useCallback, useEffect } from 'react';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';

export interface Badge {
    code: string;
    name: string;
    emoji: string;
    description: string;
    earned_at?: string;
}

export interface LevelInfo {
    level: number;
    title: string;
    current_xp: number;
    xp_for_current_level: number;
    xp_for_next_level: number | null;
    next_level_title: string | null;
}

export interface GamificationAction {
    code: string;
    name: string;
    xp_reward: number;
    category: string;
    completed: boolean;
    badge_code: string | null;
}

export interface GamificationStatus {
    total_xp: number;
    level: LevelInfo;
    badges: Badge[];
    actions: GamificationAction[];
    completed_count: number;
    total_count: number;
}

export interface TrackResult {
    success: boolean;
    action_code?: string;
    xp_earned?: number;
    total_xp?: number;
    level?: LevelInfo;
    badge_awarded?: Badge | null;
}

// Feature flag - can be disabled
const GAMIFICATION_ENABLED = true;

export const useGamification = () => {
    const { token } = useAuth();
    const [status, setStatus] = useState<GamificationStatus | null>(null);
    const [loading, setLoading] = useState(false);
    const [lastReward, setLastReward] = useState<TrackResult | null>(null);

    // Fetch current status
    const fetchStatus = useCallback(async () => {
        if (!GAMIFICATION_ENABLED || !token) return;

        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/gamification/status`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (response.ok) {
                const data = await response.json();
                setStatus(data);
            }
        } catch (error) {
            // Silently fail - gamification is non-critical
            console.debug('Gamification status fetch failed:', error);
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Track an action (fire & forget)
    const trackAction = useCallback(async (actionCode: string): Promise<TrackResult | null> => {
        if (!GAMIFICATION_ENABLED || !token) return null;

        try {
            const response = await fetch(`${API_URL}/gamification/track/${actionCode}`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                const result: TrackResult = await response.json();

                if (result.success && result.xp_earned) {
                    setLastReward(result);

                    // Update local status with new XP
                    setStatus(prev => prev ? {
                        ...prev,
                        total_xp: result.total_xp || prev.total_xp,
                        level: result.level || prev.level,
                        actions: prev.actions.map(a =>
                            a.code === actionCode ? { ...a, completed: true } : a
                        ),
                        completed_count: prev.completed_count + 1,
                    } : null);

                    return result;
                }
            }

            return null;
        } catch (error) {
            // Silently fail - never break the main action
            console.debug('Gamification tracking failed:', error);
            return null;
        }
    }, [token]);

    // Clear last reward (after showing notification)
    const clearLastReward = useCallback(() => {
        setLastReward(null);
    }, []);

    // Load status on mount
    useEffect(() => {
        fetchStatus();
    }, [fetchStatus]);

    return {
        status,
        loading,
        lastReward,
        trackAction,
        clearLastReward,
        refreshStatus: fetchStatus,
        isEnabled: GAMIFICATION_ENABLED,
    };
};

export default useGamification;
